import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  useTheme
} from '@mui/material';

const MetricsCard = ({ 
  title,
  value = 0,
  icon = null,
  color = 'primary',
  subtitle = null,
  trend = null,
  onClick = null,
  loading = false,
  details = null,
  formatNumber = true
}) => {
  const theme = useTheme();
  
  // Simple trending icons
  const TrendingUp = '📈';
  const TrendingDown = '📉';

  // Format large numbers
  const formatCount = (num) => {
    if (!formatNumber) return num.toString();
    
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Get color palette
  const getColorPalette = (colorName) => {
    const colorMap = {
      primary: {
        main: theme.palette.primary.main,
        light: theme.palette.primary.light,
        background: theme.palette.primary.main + '15'
      },
      success: {
        main: theme.palette.success.main,
        light: theme.palette.success.light,
        background: theme.palette.success.main + '15'
      },
      warning: {
        main: theme.palette.warning.main,
        light: theme.palette.warning.light,
        background: theme.palette.warning.main + '15'
      },
      error: {
        main: theme.palette.error.main,
        light: theme.palette.error.light,
        background: theme.palette.error.main + '15'
      },
      info: {
        main: theme.palette.info.main,
        light: theme.palette.info.light,
        background: theme.palette.info.main + '15'
      }
    };
    return colorMap[colorName] || colorMap.primary;
  };

  const colorPalette = getColorPalette(color);

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4
        } : {},
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
        {/* Header with icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          {/* Title */}
          <Typography
            variant="h6"
            component="div"
            color="text.secondary"
            fontWeight="medium"
            sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
          >
            {title}
          </Typography>

          {/* Icon */}
          {icon && (
            <Box
              sx={{
                width: { xs: 36, sm: 48 },
                height: { xs: 36, sm: 48 },
                borderRadius: 2,
                backgroundColor: colorPalette.background,
                color: colorPalette.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {React.cloneElement(icon, { 
                fontSize: 'medium',
                color: 'inherit'
              })}
            </Box>
          )}
        </Box>

        {/* Value - responsive font so long numbers (e.g. €12.345,67) fit on mobile */}
        <Box sx={{ mb: subtitle || trend || (details && details.length) ? 1 : 0, minWidth: 0 }}>
          {loading ? (
            <Typography
              variant="h3"
              component="div"
              sx={{ 
                fontSize: { xs: '1.1rem', sm: '1.6rem', md: '2rem', lg: '2.2rem' },
                lineHeight: 1.2,
                color: '#ccc'
              }}
            >
              ---
            </Typography>
          ) : (
            <Typography
              variant="h3"
              component="div"
              fontWeight="bold"
              color={colorPalette.main}
              sx={{ 
                fontSize: { xs: '1.1rem', sm: '1.6rem', md: '2rem', lg: '2.2rem' },
                lineHeight: 1.2
              }}
            >
              {formatCount(value)}
            </Typography>
          )}
        </Box>

        {/* Subtitle, breakdown and Trend */}
        {(subtitle || (details && details.length) || trend) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, wordBreak: 'break-word' }}
              >
                {subtitle}
              </Typography>
            )}

            {Array.isArray(details) && details.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {details.map((item) => (
                  <Typography
                    key={item.label}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  >
                    {item.label}: {item.value}
                  </Typography>
                ))}
              </Box>
            )}

            {trend && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    color: trend.value > 0 ? '#4caf50' : '#f44336'
                  }}
                >
                  {trend.value > 0 ? TrendingUp : TrendingDown}
                </span>
                <Typography
                  variant="caption"
                  color={trend.value > 0 ? 'success.main' : 'error.main'}
                  fontWeight="medium"
                >
                  {`${Math.abs(trend.value)}% ${trend.period || ''}`}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricsCard;